import { useEffect, useRef, useState } from "react";

const EXPECTED_TIME = 2;

/* ================= ANALYSIS ================= */
function getPerformanceLabel(accuracy, avgTime, c) {
  if (accuracy >= 98 && avgTime < 1 * c) return "Outstanding";
  if (accuracy >= 95 && avgTime < 2 * c) return "Excellent";
  if (accuracy >= 90 && avgTime < 3 * c) return "Very Good+";
  if (accuracy >= 85 && avgTime < 4 * c) return "Very Good";
  if (accuracy >= 80 && avgTime < 5 * c) return "Good";
  if (accuracy >= 65 && avgTime < 6 * c) return "Average";
  if (accuracy >= 50 && avgTime < 8 * c) return "Poor";
  return "Needs Improvement";
}

const PERFORMANCE_LABELS = {
  100: "🔥 Outstanding",
  90: "⭐ Excellent",
  80: "💪 Very Good+",
  70: "👍 Very Good",
  60: "🙂 Good",
  50: "😐 Average",
  40: "⚠ Poor",
  30: "🚨 Needs Improvement",
};
const graphScroll = {
  width: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "thin", // Firefox
};

const graphInner = {
  minWidth: 720, // 👈 controls horizontal scroll
  height: 320,
};

function Analysis({ history, sidebarTheme }) {
  const normalHistory = history.filter((t) => t.testType === "normal");
  const streamHistory = history.filter((t) => t.testType === "stream");

  const accuracyRef = useRef(null);
  const correctRef = useRef(null);
  const timeRef = useRef(null);
  const performanceRef = useRef(null);
  const streamTimeRef = useRef(null);
  const [latestNormal, setLatestNormal] = useState(null);

  if (!window.Chart) return null;

  const toDate = (t) =>
    t?.createdAt?.seconds
      ? new Date(t.createdAt.seconds * 1000)
      : new Date(t.createdAt);
  const orderedNormal = [...normalHistory].sort(
    (a, b) => toDate(a) - toDate(b)
  );

  const orderedStream = [...streamHistory].sort(
    (a, b) => toDate(a) - toDate(b)
  );

  useEffect(() => {
    if (history.length === 0) return;

    setLatestNormal(
      orderedNormal.length > 0 ? orderedNormal[orderedNormal.length - 1] : null
    );
    const labels = orderedNormal
      .slice()
      .reverse()
      .map((_, i) => `Test ${i + 1}`);

    const avgTimeData = orderedNormal
      .slice()
      .reverse()
      .map((t) => Number(t.avgTime || 0));
    const fastTimeData = orderedNormal
      .slice()
      .reverse()
      .map((t) => Number(t.fastest || 0));
    const slowTimeData = orderedNormal
      .slice()
      .reverse()
      .map((t) => Number(t.slowest || 0));
    const accuracyData = orderedNormal
      .slice()
      .reverse()
      .map((t) => Number(t.accuracy));
    const correctPercent = orderedNormal
      .slice()
      .reverse()
      .map((t) => (t.total ? (t.correct / t.total) * 100 : 0));

    const incorrectPercent = correctPercent.map((v) => 100 - v);

    accuracyRef.current?.chart?.destroy();
    correctRef.current?.chart?.destroy();
    timeRef.current?.chart?.destroy();
    performanceRef.current?.chart?.destroy();
    streamTimeRef.current?.chart?.destroy();

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      resizeDelay: 0,
    };
    const toDate = (d) =>
      d?.seconds ? new Date(d.seconds * 1000) : new Date(d);

    accuracyRef.current.chart = new window.Chart(accuracyRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [{ label: "Accuracy %", data: accuracyData, tension: 0.3 }],
      },
      options: {
        ...baseOptions,
        scales: { y: { min: 0, max: 100 } },
      },
    });

    correctRef.current.chart = new window.Chart(correctRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          { label: "Correct %", data: correctPercent, borderColor: "#22c55e" },
          {
            label: "Incorrect %",
            data: incorrectPercent,
            borderColor: "#ef4444",
          },
        ],
      },
      options: {
        ...baseOptions,
        scales: { y: { min: 0, max: 100 } },
      },
    });

    timeRef.current.chart = new window.Chart(timeRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          { label: "Average", data: avgTimeData, borderColor: "#3b82f6" },
          { label: "Fastest", data: fastTimeData, borderColor: "#22c55e" },
          { label: "Slowest", data: slowTimeData, borderColor: "#facc15" },
        ],
      },
      options: baseOptions,
    });

    const performanceScore = orderedNormal
      .slice()
      .reverse()
      .map((t) => {
        const label = getPerformanceLabel(
          Number(t.accuracy),
          Number(t.avgTime),
          EXPECTED_TIME
        );

        return {
          Outstanding: 100,
          Excellent: 90,
          "Very Good+": 80,
          "Very Good": 70,
          Good: 60,
          Average: 50,
          Poor: 40,
          "Needs Improvement": 30,
        }[label];
      });

    performanceRef.current.chart = new window.Chart(performanceRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Performance",
            data: performanceScore,
            borderColor: "#a855f7",
          },
        ],
      },
      options: {
        ...baseOptions,
        plugins: {
          tooltip: {
            callbacks: {
              label: (ctx) => PERFORMANCE_LABELS[ctx.parsed.y] || ctx.parsed.y,
            },
          },
        },
        scales: { y: { min: 0, max: 100 } },
      },
    });

    if (orderedStream.length > 0) {
      const streamLabels = orderedStream
        .slice()
        .reverse()
        .map((_, i) => `Stream ${i + 1}`);
      const streamTime = orderedStream.map((t) =>
        (t.timeTaken / 60).toFixed(2)
      );
      const pointColors = orderedStream.map((t) =>
        Number(t.correct) === Number(t.total) ? "#22c55e" : "#ef4444"
      );
      const results = orderedStream.map((t) =>
        Number(t.correct) === Number(t.total) ? "Correct" : "Wrong"
      );

      streamTimeRef.current.chart = new window.Chart(streamTimeRef.current, {
        type: "line",
        data: {
          labels: streamLabels,
          datasets: [
            {
              label: "Stream Time",
              data: streamTime,
              pointRadius: 6,
              pointBackgroundColor: pointColors,
              borderColor: "#64748b",
            },
          ],
        },
        options: {
          ...baseOptions,
          plugins: {
            tooltip: {
              callbacks: {
                label: (ctx) =>
                  `Time: ${ctx.parsed.y} min • ${results[ctx.dataIndex]}`,
              },
            },
          },
        },
      });
    }
    return () => {
      accuracyRef.current?.chart?.destroy();
      correctRef.current?.chart?.destroy();
      timeRef.current?.chart?.destroy();
      performanceRef.current?.chart?.destroy();
      streamTimeRef.current?.chart?.destroy();
    };
  }, [history]);

  return (
    <div>
      <h1 style={{ fontSize: 26, marginBottom: 20 }}>📈 Analysis</h1>

      {renderGraph("Accuracy", accuracyRef, sidebarTheme)}
      {renderGraph("Correct vs Incorrect", correctRef, sidebarTheme)}
      {renderGraph("Time Analysis", timeRef, sidebarTheme)}

      <div style={analysisGraph(sidebarTheme)}>
        <div style={{ marginBottom: 12 }}>
          <span style={sectionPill(sidebarTheme)}>Overall Performance</span>
        </div>
        <div style={graphScroll}>
          <div style={graphInner}>
            <canvas ref={performanceRef} />
          </div>
        </div>

        <p style={{ marginTop: 10, opacity: 0.75, textAlign: "center" }}>
          Latest Rating:{" "}
          <strong>
            {latestNormal &&
              getPerformanceLabel(
                Number(latestNormal.accuracy),
                Number(latestNormal.avgTime),
                EXPECTED_TIME
              )}
          </strong>
        </p>
      </div>

      {streamHistory.length > 0 &&
        renderGraph("Stream Test Analysis", streamTimeRef, sidebarTheme)}
    </div>
  );
}

/* ===== HELPERS ===== */

function renderGraph(title, ref, theme) {
  return (
    <div style={analysisGraph(theme)}>
      <div style={{ marginBottom: 12 }}>
        <span style={sectionPill(theme)}>{title}</span>
      </div>
      <div style={graphScroll}>
        <div style={graphInner}>
          <canvas ref={ref} />
        </div>
      </div>
    </div>
  );
}

const sectionPill = (theme) => ({
  display: "inline-block",
  padding: "8px 18px",
  borderRadius: 999,
  background:
    theme.mode === "dark" ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)",
  border: `1px solid ${theme.border}`,
  fontSize: 18,
  fontWeight: 600,
});

const analysisGraph = (theme) => ({
  background: theme.card,
  padding: 16,
  borderRadius: 14,
  marginBottom: 22,
  width: "100%",
  color: theme.text,
  border: `1px solid ${theme.border}`,
});

export default Analysis;
